import React, { useState } from "react";
import { View, ScrollView } from "react-native";
import { useRouter } from "expo-router";
import { useQuery } from "@tanstack/react-query";
import { formatRelativeTime, formatSplitDateTime } from "@/lib/dateFormat";
import { reviewApi, type ApplicationSummary } from "@/lib/api/review";
import { usersApi } from "@/lib/api/users";
import { usePagination } from "@/hooks/usePagination";
import { AppText } from "@/components/ui/AppText";
import { useI18n } from "@/context/I18nContext";
import { StatCard } from "@/components/ui/StatCard";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { Icon } from "@/components/ui/Icon";
import { Button } from "@/components/ui/Button";
import { Table, TableColumn } from "@/components/ui/Table";
import { Badge } from "@/components/ui/Badge";
import { FilterGroup, type FilterOption } from "@/components/ui/FilterGroup";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { PageFooter } from "@/components/ui/PageFooter";

// ── Screen ────────────────────────────────────────────────────────────────────

export default function StaffOverviewScreen() {
  const { t, locale } = useI18n();
  const router = useRouter();
  const { top } = useSafeAreaInsets();

  const [activeFilter, setActiveFilter] = useState("last24Hours");
  const { page, setPage, limit } = usePagination({ initialPage: 1, initialLimit: 10 });

  // Single query drives both stat cards and recent applications table.
  // activeFilter is included in the queryKey so it invalidates on change.
  // The `since` param is omitted until the backend supports time-window filtering (#218).
  const { data, isLoading } = useQuery({
    queryKey: ["review", "applications", "overview", activeFilter, limit, page],
    queryFn: () => reviewApi.list({ limit, offset: (page - 1) * limit }),
  });

  const { data: statsData } = useQuery({
    queryKey: ["admin", "stats"],
    queryFn: () => usersApi.getStats(),
  });

  const total = data?.total ?? 0;
  const pendingCount = data?.counts?.pending_review ?? 0;
  const approvedCount = data?.counts?.approved ?? 0;
  const rejectedCount = data?.counts?.rejected ?? 0;
  const items = data?.items ?? [];

  const totalInvitationsSent = statsData?.invitations.totalSent ?? 0;
  const newInvitations = statsData?.invitations.new ?? 0;
  const resentInvitations = statsData?.invitations.resent ?? 0;

  const filterOptions: FilterOption[] = [
    { id: "last24Hours", label: t("portal.dashboard.filter.last24Hours") },
    { id: "last7Days", label: t("portal.dashboard.filter.last7Days") },
    { id: "last30Days", label: t("portal.dashboard.filter.last30Days") },
    {
      id: "custom",
      label: t("portal.dashboard.filter.custom"),
      icon: (
        <Icon
          as={MaterialCommunityIcons}
          name="chevron-down"
          size={20}
          className="text-supporting-text"
        />
      ),
    },
  ];

  const columns: TableColumn<ApplicationSummary>[] = [
    {
      key: "company_name",
      header: t("portal.dashboard.table.companyName"),
      width: 320,
      renderCell: (item) => (
        <AppText weight="medium" className="text-supporting-text text-base">
          {item.company_name ?? "—"}
        </AppText>
      ),
    },
    {
      key: "contact",
      header: t("portal.dashboard.table.primaryContact"),
      width: 280,
      flex: 1,
      renderCell: (item) => (
        <View>
          <AppText weight="medium" className="text-supporting-text text-base">
            {item.primary_contact_name ?? "—"}
          </AppText>
          <AppText className="text-dark-text-20 text-sm">
            {item.primary_contact_email ?? item.vendor_email ?? ""}
          </AppText>
        </View>
      ),
    },
    {
      key: "primary_industry",
      header: t("portal.dashboard.table.primaryIndustry"),
      width: 220,
      renderCell: (item) => (
        <AppText weight="medium" className="text-supporting-text text-base">
          {item.primary_industry ?? "—"}
        </AppText>
      ),
    },
    {
      key: "status",
      header: t("portal.dashboard.table.status"),
      width: 180,
      renderCell: (item) => {
        const variantMap = {
          approved: "filled-success",
          pending_review: "filled-warning",
          rejected: "filled-error",
          invited: "accent-blue",
        } as const;
        const labelMap = {
          approved: "portal.dashboard.approved",
          pending_review: "portal.dashboard.pendingReview",
          rejected: "portal.dashboard.rejected",
          invited: "portal.dashboard.invited",
        } as const;
        return (
          <View className="items-start">
            <Badge variant={variantMap[item.status]} label={labelMap[item.status]} />
          </View>
        );
      },
    },
    {
      key: "last_activity",
      header: t("portal.dashboard.table.lastActivity"),
      width: 240,
      renderCell: (item) => {
        const ts = item.last_activity ?? item.submitted_at;
        if (!ts) return <AppText className="text-supporting-text text-base">{"—"}</AppText>;
        const { date, time } = formatSplitDateTime(ts, locale);
        return (
          <>
            <AppText weight="medium" className="text-supporting-text text-base" numberOfLines={1}>
              {formatRelativeTime(ts, locale, t)}
            </AppText>
            <View className="flex-row items-center gap-1 mt-0.5">
              <AppText className="text-dark-text-20 text-sm">{date}</AppText>
              <View className="w-1 h-1 rounded-full bg-stroke-30" />
              <AppText className="text-dark-text-20 text-sm">{time}</AppText>
            </View>
          </>
        );
      },
    },
    {
      key: "actions",
      header: "",
      width: 140,
      renderCell: (item) => (
        <View className="items-end py-2">
          <Button
            label="common.view"
            variant="secondary"
            onPress={() => router.push(`/(staff)/applications/${item.id}`)}
            className="py-2 h-[42px] border-stroke-30 rounded"
            labelClassName="hover:text-primary text-supporting-text"
          />
        </View>
      ),
    },
  ];

  return (
    <View className="flex-1 bg-stroke-10">
      <ScrollView
        className="flex-1 bg-white"
        testID="staff-dashboard"
        contentContainerStyle={{ flexGrow: 1, paddingTop: top }}
      >
        {/* Header section */}
        <View className="flex-col flex-wrap md:flex-row items-start md:items-center justify-between gap-4 px-page-x tablet:px-20 pt-10 mb-10">
          <View className="flex-col gap-2 items-start">
            <AppText
              weight="bold"
              className="text-[29px] leading-[31.9px] tracking-[-0.29px] text-dark-text-50"
            >
              {t("portal.dashboard.overview")}
            </AppText>
            <AppText weight="medium" className="text-base text-supporting-text">
              {t("portal.dashboard.subtitle")}
            </AppText>
          </View>

          <FilterGroup options={filterOptions} value={activeFilter} onChange={setActiveFilter} />
        </View>

        <View className="h-px bg-stroke-50 w-full mb-10" />

        {/* ── Application stat cards ────────────────────────────── */}
        <View className="flex-col gap-4 mb-10 px-page-x tablet:px-20">
          <AppText
            weight="semibold"
            className="text-sm tracking-[0.14px] text-dark-text-20 uppercase"
          >
            {t("portal.dashboard.applications")}
          </AppText>
          <View className="flex-col tablet:flex-row tablet:flex-wrap gap-4">
            <StatCard
              title={t("portal.dashboard.applicationsSubmitted")}
              value={isLoading ? "—" : String(total)}
              icon={
                <Icon
                  as={MaterialCommunityIcons}
                  name="file-document-outline"
                  size={20}
                  className="text-blue-50"
                />
              }
              iconBgClass="bg-blue-5"
            />
            <StatCard
              title={t("portal.dashboard.pendingReview")}
              value={isLoading ? "—" : String(pendingCount)}
              icon={
                <Icon
                  as={MaterialCommunityIcons}
                  name="clock-outline"
                  size={20}
                  className="text-orange-50"
                />
              }
              iconBgClass="bg-orange-light"
            />
            <StatCard
              title={t("portal.dashboard.approved")}
              value={isLoading ? "—" : String(approvedCount)}
              icon={
                <Icon
                  as={MaterialCommunityIcons}
                  name="check-circle-outline"
                  size={20}
                  className="text-green-50"
                />
              }
              iconBgClass="bg-success-light"
            />
            <StatCard
              title={t("portal.dashboard.rejected")}
              value={isLoading ? "—" : String(rejectedCount)}
              icon={
                <Icon
                  as={MaterialCommunityIcons}
                  name="close-circle-outline"
                  size={20}
                  className="text-error"
                />
              }
              iconBgClass="bg-error-light"
            />
          </View>
        </View>

        {/* ── Invitation stat cards ── */}
        <View className="flex-col gap-4 mb-10 px-page-x tablet:px-20">
          <AppText
            weight="semibold"
            className="text-sm tracking-[0.14px] text-dark-text-20 uppercase"
          >
            {t("portal.dashboard.invitations")}
          </AppText>
          <View className="flex-col tablet:flex-row tablet:flex-wrap gap-4">
            <StatCard
              title={t("portal.dashboard.allInvitationsSent")}
              value={totalInvitationsSent}
              icon={
                <Icon
                  as={MaterialCommunityIcons}
                  name="email-outline"
                  size={20}
                  className="text-green-50"
                />
              }
              iconBgClass="bg-success-light"
            />
            <StatCard
              title={t("portal.dashboard.newInvitations")}
              value={newInvitations}
              icon={
                <Icon
                  as={MaterialCommunityIcons}
                  name="email-plus-outline"
                  size={20}
                  className="text-blue-50"
                />
              }
              iconBgClass="bg-blue-5"
            />
            <StatCard
              title={t("portal.dashboard.resentInvitations")}
              value={resentInvitations}
              icon={
                <Icon
                  as={MaterialCommunityIcons}
                  name="email-sync-outline"
                  size={20}
                  className="text-orange-50"
                />
              }
              iconBgClass="bg-orange-light"
            />
          </View>
        </View>

        <View className="h-px bg-stroke-50 w-full" />

        {/* ── Recent applications table ─────────────────────────── */}
        <View className="w-full flex-col mt-10 px-page-x tablet:px-20 mb-10">
          <View className="flex-row flex-wrap gap-4 items-center justify-between mb-4">
            <AppText weight="semibold" className="text-xl text-dark-text-50">
              {t("portal.dashboard.recentApplications")}
            </AppText>
            <Button
              label="portal.dashboard.viewAllApplications"
              variant="primary"
              onPress={() => router.push("/(staff)/(dashboard)/applications")}
            />
          </View>
          <Table
            columns={columns}
            data={items}
            keyExtractor={(item) => item.id}
            minWidth={1160}
            limit={limit}
            totalCount={total}
            page={page}
            isLoading={isLoading}
            onPageChange={(p) => setPage(p)}
            onRowPress={(item) => router.push(`/(staff)/applications/${item.id}`)}
          />
        </View>

        <PageFooter />
      </ScrollView>
    </View>
  );
}
